import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faClose } from '@fortawesome/free-solid-svg-icons';
import { withAppHOC } from '../../hoc';
import {
  StyledContentContainer,
  StyledMainAreaContainer,
  StyledSpan,
} from '../../components/styled';
import PageHeader from '../../components/PageHeader/PageHeader';
import { Image } from '../../components/Image';
import {
  Form,
  Fieldset,
  Label,
  TextInput,
  MobileInput,
  Dropdown,
  MessageBox,
  Button,
} from '../../components/FormElements';
import API from '../../api';
import {
  clearLocalStorage,
  formatCurrency,
  getAuthData,
  getBase64,
  setLocalStorage,
} from '../../utils';
import { RESPONSE_STATUSES } from '../../constants';

const StyledContent = styled.p`
  color: green;
  display: block;
  flex: 2;
  text-transform: uppercase;
`;

const StyledPhotoContainer = styled.section`
  width: auto;
  height: fit-content;
  @media (max-width: 600px) {
    max-width: 180px;
  }
`;

const StyledPhotoImageContainer = styled.section`
  width: 150px;
  height: 150px;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  padding: 10px;
  border: 5px solid
    ${({ theme: { colors } }) => colors.primaryColor || `#3049f8`};
  border-bottom: 0px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
`;

// 🔹 NEW: Email mandatory popup overlay
const EmailModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

// 🔹 NEW: Email mandatory popup box
const EmailModalContent = styled.div`
  background: #ffffff;
  padding: 24px;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  text-align: center;
`;

const StyledPhotoSelector = styled.section`
  width: auto;
`;

const StyledPhotoButton = styled.button`
  border-width: 0px;
  background-color: ${({ theme: { colors } }) =>
    colors.primaryColor || `#3049f8`};
  padding: 15px 10px;
  width: 100%;
  font-size: 14px;
  font-weight: normal;
  color: #ffffff;
  display: flex;
  flex-direction: row;
  column-gap: 10px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  &:hover {
    background-color: #008a7d;
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const StyledFormContainer = styled.section`
  width: 100%;
  max-width: 500px;
  flex: 1;
`;

const StyledInput = styled.input`
  display: none;
`;

const Profile = (props) => {
  const {
    appData: { accessType },
  } = props;
  const {
    userProfile: {
      gstWalletStatus,
      gstInvoiceP2AStatus,
      gstInvoiceP2PStatus,
      sellerAmountHoldPercentage,
    },
  } = getAuthData();
  const gstTypeOptions = [{ name: '-- SELECT GST TYPE --', value: '' }];
  if (gstInvoiceP2PStatus && gstInvoiceP2PStatus === 'Active') {
    gstTypeOptions.push({
      name: 'P2P',
      value: 'P2P',
    });
  }
  if (gstInvoiceP2AStatus && gstInvoiceP2AStatus === 'Active') {
    gstTypeOptions.push({
      name: 'P2A',
      value: 'P2A',
    });
  }
  const inputRef = useRef(null);
  const [state, setState] = useState({
    isLoading: false,
    responseStatus: '',
    responseMessage: '',
    fullName: '',
    email: '',
    mobileNumber: '',
    gender: '',
    dateOfBirth: '',
    photo: '',
    photoBase64: '',
    panName: '',
    panNumber: '',
    aadhaarName: '',
    aadhaarNumber: '',
    isGSTSeller: '',
    gstName: '',
    gstNumber: '',
    gstFile: '',
    gstType: 'P2P',
    autoWalletTransferStatus: '',
    autoWalletTransferAmount: '',
  });
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  // 🔹 NEW: Show popup if email is missing
  useEffect(() => {
    if (!state.email || !state.email.trim()) {
      setShowEmailPopup(true);
    } else {
      setShowEmailPopup(false);
    }
  }, [state.email]);

  const readData = async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      responseStatus: '',
      responseMessage: '',
    }));
    try {
      const response = await API.get('/authentication/profile');
      const { status, message, data } = response.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setState((prevState) => ({
          ...prevState,
          responseStatus: '',
          responseMessage: '',
          fullName: data?.fullName,
          email: data?.email,
          mobileNumber: data?.mobileNumber,
          gender: data?.gender,
          dateOfBirth: data?.dateOfBirth,
          photo: data?.photo,
          panName: data?.panName,
          panNumber: data?.panNumber,
          aadhaarName: data?.aadhaarName,
          aadhaarNumber: data?.aadhaarNumber,
          isGSTSeller: data?.isGSTSeller,
          gstName: data?.gstName,
          gstNumber: data?.gstNumber,
          gstFile: data?.gstFile,
          gstType: data?.gstType,
          autoWalletTransferStatus: data?.autoWalletTransferStatus,
          autoWalletTransferAmount: data?.autoWalletTransferAmount,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.FAILED,
          responseMessage: message,
        }));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error.message,
      }));
    } finally {
      setState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  // const fetchGst = async (e) => {
  //   e.preventDefault();
  //   setState((prevState) => ({
  //     ...prevState,
  //     isLoading: true,
  //     responseStatus: "",
  //     responseMessage: "",
  //   }));
  //   try {
  //     const response = await API.post("/authentication/fetch-gst", {
  //       gstNumber: state.gstNumber,
  //     });
  //     const { status, message, data, actualData } = response.data;
  //     if (status === RESPONSE_STATUSES.SUCCESS) {
  //       setState((prevState) => ({
  //         ...prevState,
  //         responseStatus: RESPONSE_STATUSES.SUCCESS,
  //         responseMessage: message,
  //         gstName: data,
  //         gstFile: actualData,
  //       }));
  //       setTimeout(() => {
  //         setState((prevState) => ({
  //           ...prevState,
  //           responseStatus: "",
  //           responseMessage: "",
  //         }));
  //       }, 3000);
  //     } else {
  //       setState((prevState) => ({
  //         ...prevState,
  //         responseStatus: RESPONSE_STATUSES.FAILED,
  //         responseMessage: message,
  //       }));
  //     }
  //   } catch (error) {
  //     setState((prevState) => ({
  //       ...prevState,
  //       responseStatus: RESPONSE_STATUSES.FAILED,
  //       responseMessage: error.message,
  //     }));
  //   } finally {
  //     setState((prevState) => ({ ...prevState, isLoading: false }));
  //   }
  // };

  const onSubmit = async (e) => {
    e.preventDefault();
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      responseStatus: '',
      responseMessage: '',
    }));
    try {
      const response = await API.put('/authentication/profile', {
        fullName: state.fullName,
        email: state.email,
        mobileNumber: state.mobileNumber,
        gender: state.gender,
        dateOfBirth: state.dateOfBirth,
        photo:
          state.photoBase64 && !isEmpty(state.photoBase64)
            ? state.photoBase64
            : state.photo,
        isGSTSeller: state.isGSTSeller,
        gstName: state.gstName,
        gstNumber: state.gstNumber,
        gstFile:
          state.gstFile && typeof state.gstFile !== 'string'
            ? JSON.stringify(state.gstFile)
            : state.gstFile,
        gstType: state.gstType,
        autoWalletTransferStatus: ['V1'].includes(accessType)
          ? 'No'
          : state.autoWalletTransferStatus,
        autoWalletTransferAmount: ['V1'].includes(accessType)
          ? '0'
          : state.autoWalletTransferAmount,
      });
      const { status, message, data, shouldReload } = response.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setLocalStorage('userProfile', JSON.stringify(data));
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.SUCCESS,
          responseMessage: message,
        }));
        setTimeout(() => {
          setState((prevState) => ({
            ...prevState,
            responseStatus: '',
            responseMessage: '',
          }));
          if (shouldReload) {
            clearLocalStorage();
            window.location.href = '/login';
          }
        }, 3000);
      } else {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.FAILED,
          responseMessage: message,
        }));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error.message,
      }));
    } finally {
      setState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  const openFileSelector = () => {
    inputRef.current.click();
  };

  useState(() => {
    readData();
  }, []);

  return (
    <>
      <PageHeader title="Your Profile" />
      <StyledContentContainer>
        <StyledMainAreaContainer>
          <StyledPhotoContainer>
            <StyledPhotoImageContainer>
              <Image
                source={
                  state.photoBase64
                    ? state.photoBase64
                    : state.photo
                      ? state.photo
                      : 'default-profile-photo.png'
                }
                alt={'Seelam Sivasankar Naidu'}
              />
            </StyledPhotoImageContainer>
            <StyledPhotoSelector>
              <StyledInput
                type="file"
                ref={inputRef}
                onChange={(e) => {
                  getBase64(e.target.files[0], (photoBase64) => {
                    setState((prevState) => ({
                      ...prevState,
                      photoBase64: photoBase64,
                    }));
                  });
                }}
                accept="image/*"
                disabled={state.isLoading}
              />
              <StyledPhotoButton
                disabled={state.isLoading}
                onClick={() => {
                  if (!state.isLoading) {
                    if (!isEmpty(state.photoBase64) || !isEmpty(state.photo)) {
                      inputRef.current.value = null;
                      setState((prevState) => ({
                        ...prevState,
                        photoBase64: '',
                        photo: '',
                      }));
                    } else {
                      openFileSelector();
                    }
                  }
                }}
              >
                {!isEmpty(state.photoBase64) || !isEmpty(state.photo) ? (
                  <>
                    <FontAwesomeIcon icon={faClose} /> Remove Photo
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCamera} /> Select Photo
                  </>
                )}
              </StyledPhotoButton>
            </StyledPhotoSelector>
          </StyledPhotoContainer>
          <StyledFormContainer>
            <Form action="#" method="POST" onSubmit={onSubmit}>
              <Fieldset>
                <Label required>Full Name</Label>
                <TextInput
                  value={state.fullName}
                  onChange={(value) =>
                    setState((prevState) => ({ ...prevState, fullName: value }))
                  }
                  placeholder="Full Name"
                  disabled={true}
                />
              </Fieldset>
              <Fieldset>
                <Label required>Mobile Number</Label>
                <MobileInput
                  value={state.mobileNumber}
                  onChange={(value) =>
                    setState((prevState) => ({
                      ...prevState,
                      mobileNumber: value,
                    }))
                  }
                  placeholder="Mobile Number"
                  disabled={true}
                />
              </Fieldset>
              <Fieldset>
                <Label required>Email</Label>
                <TextInput
                  value={state.email}
                  onChange={(value) =>
                    setState((prevState) => ({ ...prevState, email: value }))
                  }
                  placeholder="Email"
                  disabled={false}
                />
              </Fieldset>
              <Fieldset>
                <Label required>Gender</Label>
                <TextInput
                  value={state.gender}
                  onChange={(value) =>
                    setState((prevState) => ({
                      ...prevState,
                      gender: value,
                    }))
                  }
                  placeholder="Gender"
                  disabled={true}
                />
              </Fieldset>
              <Fieldset>
                <Label required>Date Of Birth</Label>
                <TextInput
                  value={state.dateOfBirth}
                  onChange={(value) =>
                    setState((prevState) => ({
                      ...prevState,
                      dateOfBirth: value,
                    }))
                  }
                  placeholder="Date Of Birth"
                  disabled={true}
                />
              </Fieldset>
              <Fieldset>
                <Label required>PAN Name</Label>
                <TextInput
                  value={state.panName}
                  onChange={(value) =>
                    setState((prevState) => ({ ...prevState, panName: value }))
                  }
                  placeholder="PAN Name"
                  disabled={true}
                />
              </Fieldset>
              <Fieldset>
                <Label required>PAN Number</Label>
                <TextInput
                  value={state.panNumber}
                  onChange={(value) =>
                    setState((prevState) => ({
                      ...prevState,
                      panNumber: value,
                    }))
                  }
                  placeholder="PAN Number"
                  disabled={true}
                />
              </Fieldset>
              <Fieldset>
                <Label required>Aadhaar Name</Label>
                <TextInput
                  value={state.aadhaarName}
                  onChange={(value) =>
                    setState((prevState) => ({
                      ...prevState,
                      aadhaarName: value,
                    }))
                  }
                  placeholder="Aadhaar Name"
                  disabled={true}
                />
              </Fieldset>
              <Fieldset>
                <Label required>Aadhaar Number</Label>
                <TextInput
                  value={state.aadhaarNumber}
                  onChange={(value) =>
                    setState((prevState) => ({
                      ...prevState,
                      aadhaarNumber: value,
                    }))
                  }
                  placeholder="Aadhaar Number"
                  disabled={true}
                />
              </Fieldset>
              {gstWalletStatus === 'Active' && (
                <>
                  <Fieldset>
                    <Label required>Do you have GST ?</Label>
                    <Dropdown
                      value={state.isGSTSeller}
                      onChange={(value) =>
                        setState((prevState) => ({
                          ...prevState,
                          isGSTSeller: value,
                          gstNumber: '',
                          gstName: '',
                          gstFile: '',
                          gstType: '',
                        }))
                      }
                      placeholder="GST Status"
                      // disabled={state.isLoading}
                      disabled={true}
                      options={[
                        {
                          name: '-- SELECT GST STATUS --',
                          value: '',
                        },
                        {
                          name: 'Yes',
                          value: 'Yes',
                        },
                        {
                          name: 'No',
                          value: 'No',
                        },
                      ]}
                    />
                  </Fieldset>
                  {state.isGSTSeller === 'Yes' && (
                    <>
                      <Fieldset>
                        <Label required>GST Number</Label>
                        <TextInput
                          value={state.gstNumber}
                          onChange={(value) =>
                            setState((prevState) => ({
                              ...prevState,
                              gstNumber: value,
                              gstName: '',
                              gstFile: '',
                            }))
                          }
                          placeholder="GST Number"
                          // disabled={state.isLoading}
                          disabled={true}
                        />
                      </Fieldset>
                      {state.gstName && (
                        <Fieldset>
                          <Label>Name on GST</Label>
                          <StyledContent>{state.gstName}</StyledContent>
                        </Fieldset>
                      )}
                      {/* <Fieldset>
                        <Button
                          onClick={fetchGst}
                          disabled={state.gstName || state.isLoading}
                        >
                          Fetch Details
                        </Button>
                      </Fieldset> */}
                      {/* <Fieldset>
                        <Label required>GST Type</Label>
                        <Dropdown
                          value={state.gstType}
                          onChange={(value) =>
                            setState((prevState) => ({
                              ...prevState,
                              gstType: value,
                            }))
                          }
                          placeholder="GST Type"
                          disabled={state.isLoading}
                          options={gstTypeOptions}
                        />
                      </Fieldset> */}
                    </>
                  )}
                </>
              )}
              {!['V1'].includes(accessType) && (
                <Fieldset>
                  <Label required>Auto Wallet Transfer</Label>
                  <Dropdown
                    value={state.autoWalletTransferStatus}
                    onChange={(value) =>
                      setState((prevState) => ({
                        ...prevState,
                        autoWalletTransferStatus: value,
                      }))
                    }
                    placeholder="Auto Wallet Transfer"
                    disabled={state.isLoading}
                    options={[
                      {
                        name: '-- SELECT GST STATUS --',
                        value: '',
                      },
                      {
                        name: 'Yes',
                        value: 'Yes',
                      },
                      {
                        name: 'No',
                        value: 'No',
                      },
                    ]}
                  />
                </Fieldset>
              )}
              {!['V1'].includes(accessType) &&
                state.autoWalletTransferStatus === 'Yes' && (
                  <>
                    <Fieldset>
                      <Label required>Auto Transfer Amount</Label>
                      <TextInput
                        isNumeric
                        value={state.autoWalletTransferAmount}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            autoWalletTransferAmount: value,
                          }))
                        }
                        placeholder="Auto Wallet Transfer Amount"
                        disabled={state.isLoading}
                      />
                    </Fieldset>
                    {state.autoWalletTransferAmount &&
                      state.autoWalletTransferAmount > 0 && (
                        <Fieldset>
                          <StyledSpan
                            style={{ color: 'red', fontSize: '14px' }}
                          >
                            Amount will be auto Trasnferred when seller balance
                            reaches{' '}
                            {sellerAmountHoldPercentage &&
                              sellerAmountHoldPercentage > 0
                              ? formatCurrency(
                                parseFloat(state.autoWalletTransferAmount) +
                                parseFloat(
                                  (parseFloat(
                                    state.autoWalletTransferAmount
                                  ) /
                                    100) *
                                  sellerAmountHoldPercentage
                                )
                              )
                              : formatCurrency(state.autoWalletTransferAmount)}
                          </StyledSpan>
                        </Fieldset>
                      )}
                  </>
                )}
              {state.responseStatus && state.responseMessage && (
                <Fieldset>
                  <MessageBox
                    status={state.responseStatus}
                    message={state.responseMessage}
                  />
                </Fieldset>
              )}
              <Fieldset>
                <Button disabled={state.isLoading}>Save</Button>
              </Fieldset>
            </Form>
          </StyledFormContainer>
        </StyledMainAreaContainer>
      </StyledContentContainer>
      {showEmailPopup && (
        <EmailModalOverlay>
          <EmailModalContent>
            <h3>Email Required</h3>
            <p>Please update a valid email address to continue.</p>

            <div style={{ marginTop: 16 }}>
              <Button
                onClick={() => setShowEmailPopup(false)}
              >
                Continue
              </Button>
            </div>
          </EmailModalContent>
        </EmailModalOverlay>
      )}
    </>
  );
};

export default withAppHOC(Profile);
